import { Controller } from '@hotwired/stimulus'
import throttle from 'lodash/throttle'
import { AJAXError } from 'utils/ajax'
import VideoDataStore from 'models/video_data_store'
import htmx from 'htmx.org'

export default class extends Controller {
  static values = {
    playUrl: String,
    viewsUrl: String,
    viewDataUrl: String
  }

  initialize() {
    this.ready = false

    // use this modified function to prevent sending the XHR request multiple times.
    // Use trailing: false so we only fire on the first event, then ignore any more until the timeout is reached,
    // otherwise we'd always fire another one at the end of the timeout.
    this.throttledUpdateLastWatchedSecond = throttle(this._updateLastWatchedSecond, 30 * 1000, { trailing: false })
  }

  onReady() {
    const viewData = JSON.parse(document.getElementById("video_view_data_json").innerHTML)
    this.dataStore.setFromViewData(viewData)

    const lastWatchedSecond = this.dataStore.getLastWatchedSecond()
    if (lastWatchedSecond > 1) {
      // Keep thumbnail if the user didn't see at least 1 sec
      // Avoid starting and pausing the video at sec 0.
      this.video.time(lastWatchedSecond)
    }
    this.ready = true

    this.element.dispatchEvent(new CustomEvent(`video-tracking:ready`, { detail: { video: this.video }, bubbles: true }))
  }

  onPlay() {
    if (!this.ready) { return }

    this._log("Video 'play' event fired")
    if (this.playUrlValue === "") { return }
    htmx.ajax("post", this.playUrlValue, { swap: "none", source: this.element, values: { showAlertOnError: true } })
  }

  onCompletion() {
    if (!this.ready) { return }

    if (this.viewsUrlValue === "") { return }
    htmx.ajax("post", this.viewsUrlValue, { swap: "none", source: this.element, values: { showAlertOnError: true } })
  }

  onPause() {
    if (!this.ready) { return }

    console.log("pause was fired")
    this._log("Video 'pause' event fired")
    this._updateLastWatchedSecond()
  }

  onVisibilityChange() {
    if (!this.ready) { return }

    if(document.visibilityState === "hidden") {
      if(this.video.state() === "playing") {
        this._updateLastWatchedSecond({ showAlertOnError: false })
      }
    }
  }

  onPageHide() {
    if (!this.ready) { return }

    if(this.video.state() === "playing") {
      this._updateLastWatchedSecond({ showAlertOnError: false })
    }
  }

  onModalClose() {
    if (!this.ready) { return }

    this._updateLastWatchedSecond()
  }

  onMouseMove() {
    if (!this.ready) { return }

    this._updateLastActiveSecond()
  }
  onTouchStart() {
    if (!this.ready) { return }

    this._updateLastActiveSecond()
  }
  onScroll() {
    if (!this.ready) { return }

    this._updateLastActiveSecond()
  }

  onResponseError(e) {
    e.stopPropagation() // Prevents the default HTMX responseError message (event_handlers.js)

    this.video.pause()

    const { xhr, requestConfig, error } = e.detail

    if (requestConfig.parameters.showAlertOnError) {
      let message
      if (xhr.status.toString().startsWith("5")) { /* Reached servers, but returned server error */
        message = `Unexpected server error occurred, please refresh the page and try again.`
      } else {
        message = `It looks like you're not connected to the internet, and we can't save your progress. Please make sure you have a stable internet connection and refresh the page.`
      }

      alert(`${message} If this problem persists, please contact support@quimbee.com. (Error code: ${xhr.status})`)
    } else {
      // Do nothing if it is a 401 or 403 error (invalid authenticity token or forbidden or similar)
      if (xhr.status === 401 || xhr.status === 403)
        return

      throw new AJAXError(`${requestConfig.verb.toUpperCase()} ${requestConfig.path} (${error})`)
    }
  }

  get video() {
    return this.element.wistiaVideo
  }

  get dataStore() {
    return VideoDataStore.getStore(this.video.hashedId())
  }

  _updateLastActiveSecond() {
    if (!this.video) { return }

    this.dataStore.setLastActiveSecond(this.video.time())
    this.throttledUpdateLastWatchedSecond()
  }

  _updateLastWatchedSecond(options={}) {
    if (!this.video) { return }
    if (this.viewDataUrlValue === "") { return }

    let lastWatchedSecond = Math.trunc(this.video.time())
    if(lastWatchedSecond === Math.trunc(this.video.duration())) {
      // If video finished we reset the last watched second
      lastWatchedSecond = 0
    }

    this.dataStore.setLastWatchedSecond(lastWatchedSecond)

    const showAlertOnError = options.showAlertOnError === undefined ? true : options.showAlertOnError

    const values = {
      'showAlertOnError': showAlertOnError,
      'view_data[last_watched_second]': lastWatchedSecond,
      'view_data[max_watched_second]': this.dataStore.getMaxWatchedSecond()
    }

    htmx.ajax("post", this.viewDataUrlValue, { swap: "none", values: values, source: this.element })
  }

  _log(message) {
    const rollbarTelemeter = window.Rollbar && window.Rollbar.captureEvent.bind(window.Rollbar)
    if(!rollbarTelemeter) { return }

    // we don't want logging to ever _cause_ errors, so try/catch as a fail safe
    try {
      rollbarTelemeter({
        message: message,
        videoTime: this.video.time()
      }, "info")
    } catch(e) {
      console.log(e)
    }
  }
}
